// ============================================
// STATE - Cart Store (Carrito POS)
// ============================================

import { create } from 'zustand';
import type { ProductoVendible, PackDetalle } from '../domain/entities';

// Función helper para redondear precios y evitar errores de punto flotante
const roundPrice = (value: number): number => {
  return Math.round(value);
};

// Monto mínimo (CLP) que debe quedar en una línea después de aplicar un descuento
const MIN_LINE_TOTAL_AFTER_DISCOUNT = 1;

export interface CartItem {
  producto: ProductoVendible;
  cantidad: number;
  subtotal: number;
  packDetalle?: PackDetalle; // Información del pack si es un pack
  descuento?: number; // Descuento aplicado (ej: para packs)
}

interface CartState {
  items: CartItem[];
  selectedItemIndex: number | null;

  // Actions
  addItem: (producto: ProductoVendible, cantidad?: number, subtotalOverride?: number) => void;
  addPackItem: (producto: ProductoVendible, packDetalle: PackDetalle) => void;
  addCalculatorItem: (total: number) => void;
  updateQuantity: (productoAlmacenId: number, cantidad: number) => void;
  setItemDiscount: (productoAlmacenId: number, descuento: number) => void;
  removeItem: (productoAlmacenId: number) => void;
  clearCart: () => void;
  selectItem: (index: number | null) => void;
  
  // Computed
  getTotal: () => number;
  getItemCount: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  selectedItemIndex: null,

  addItem: (producto, cantidad = 1, subtotalOverride?: number) => {
    const items = get().items;
    const existingIndex = items.findIndex(
      (item) => item.producto.productoAlmacenId === producto.productoAlmacenId
    );

    if (existingIndex !== -1) {
      const updatedItems = [...items];
      const item = updatedItems[existingIndex];
      const newCantidad = item.cantidad + cantidad;
      const precio = item.producto.precioVentaActual || 0;
      const addedSubtotal = subtotalOverride ?? roundPrice(cantidad * precio);

      updatedItems[existingIndex] = {
        ...item,
        cantidad: newCantidad,
        subtotal: roundPrice(item.subtotal + addedSubtotal),
      };

      set({ items: updatedItems });
    } else {
      const precio = producto.precioVentaActual || 0;
      set({
        items: [
          ...items,
          {
            producto,
            cantidad,
            subtotal: subtotalOverride ?? roundPrice(cantidad * precio),
          },
        ],
      });
    }
  },

  addPackItem: (producto, packDetalle) => {
    const items = get().items;
    
    // Buscar si el pack ya existe en el carrito
    const existingIndex = items.findIndex(
      (item) => item.producto.productoAlmacenId === producto.productoAlmacenId && item.packDetalle
    );

    const precio = packDetalle.precioPackTotal;
    const descuento = packDetalle.ahorroTotal;

    if (existingIndex !== -1) {
      // Pack ya existe, incrementar cantidad
      const updatedItems = [...items];
      const item = updatedItems[existingIndex];
      const newCantidad = item.cantidad + 1;
      
      updatedItems[existingIndex] = {
        ...item,
        cantidad: newCantidad,
        subtotal: roundPrice(newCantidad * precio),
        descuento: newCantidad * descuento,
      };
      
      set({ items: updatedItems });
    } else {
      // Nuevo pack
      set({
        items: [
          ...items,
          {
            producto,
            cantidad: 1,
            subtotal: roundPrice(precio),
            packDetalle,
            descuento,
          },
        ],
      });
    }
  },

  addCalculatorItem: (total) => {
    const items = get().items;
    
    // Crear un producto temporal para items sin descripción desde calculadora
    const productoSinDescripcion: ProductoVendible = {
      productoAlmacenId: Date.now(), // ID temporal único basado en timestamp
      productoId: -1,
      productoNombre: '(Sin descripción)',
      codigo: '',
      esPack: false,
      imagenUrl: null,
      precioCompraActual: null,
      precioVentaActual: total, // El total ingresado es el precio
      esRetail: true,
      venderPor: 'U',
      stock: 0,
      categoriaAlmacen: null,
      categoriaGlobal: null,
    };

    // Agregar como nuevo item con cantidad 1
    set({
      items: [
        ...items,
        {
          producto: productoSinDescripcion,
          cantidad: 1,
          subtotal: roundPrice(total),
        },
      ],
    });
  },

  updateQuantity: (productoAlmacenId, cantidad) => {
    if (cantidad <= 0) {
      get().removeItem(productoAlmacenId);
      return;
    }

    const items = get().items.map((item) => {
      if (item.producto.productoAlmacenId === productoAlmacenId) {
        // Si es pack, recalcular con precio del pack
        if (item.packDetalle) {
          const precioPack = item.packDetalle.precioPackTotal;
          const ahorroUnitario = item.packDetalle.ahorroTotal;
          return {
            ...item,
            cantidad,
            subtotal: roundPrice(cantidad * precioPack),
            descuento: cantidad * ahorroUnitario,
          };
        }
        
        // Si es producto normal, mantener el descuento aplicado (en pesos), sin dejar la línea bajo
        // el mínimo cuando baja la cantidad
        const precio = item.producto.precioVentaActual || 0;
        const subtotalSinDescuento = roundPrice(cantidad * precio);
        const descuentoMaximo = Math.max(subtotalSinDescuento - MIN_LINE_TOTAL_AFTER_DISCOUNT, 0);
        const descuentoActual = Math.min(item.descuento || 0, descuentoMaximo);
        return {
          ...item,
          cantidad,
          descuento: item.descuento ? descuentoActual : item.descuento,
          subtotal: roundPrice(subtotalSinDescuento - descuentoActual),
        };
      }
      return item;
    });

    set({ items });
  },

  setItemDiscount: (productoAlmacenId, descuento) => {
    const items = get().items.map((item) => {
      if (item.producto.productoAlmacenId === productoAlmacenId && !item.packDetalle) {
        const precio = item.producto.precioVentaActual || 0;
        const subtotalSinDescuento = roundPrice(item.cantidad * precio);
        // El descuento no puede ser igual o mayor al subtotal (debe quedar al menos $1)
        const descuentoFinal = Math.min(descuento, subtotalSinDescuento - MIN_LINE_TOTAL_AFTER_DISCOUNT);
        return {
          ...item,
          descuento: descuentoFinal,
          subtotal: roundPrice(subtotalSinDescuento - descuentoFinal),
        };
      }
      return item;
    });

    set({ items });
  },

  removeItem: (productoAlmacenId) => {
    set({
      items: get().items.filter(
        (item) => item.producto.productoAlmacenId !== productoAlmacenId
      ),
      selectedItemIndex: null,
    });
  },

  clearCart: () => {
    set({
      items: [],
      selectedItemIndex: null,
    });
  },

  selectItem: (index) => {
    set({ selectedItemIndex: index });
  },

  getTotal: () => {
    return get().items.reduce((sum, item) => sum + item.subtotal, 0);
  },

  getItemCount: () => {
    return get().items.reduce((sum, item) => {
      // Si es pack o se vende por unidad, contar la cantidad exacta
      if (item.packDetalle || item.producto.venderPor === 'U') {
        return sum + item.cantidad;
      }
      // Si se vende por fracción/granel, contar como 1 producto
      return sum + 1;
    }, 0);
  },
}));
